import type { MessageTree } from '../types';

/** English UI copy — catalog payloads stay in their original language. */
export const en: MessageTree = {
  brand: {
    tagline: 'Payment Explorer',
  },
  ad: {
    label: 'Advertisement',
  },
  consent: {
    title: 'Cookies and ads',
    body: 'We use Google AdSense on catalog pages. Accept personalized ads, or continue with non-personalized ads only. You can read how we use cookies in',
    privacy: 'Privacy',
    accept: 'Accept personalized ads',
    reject: 'Non-personalized only',
  },
  footer: {
    version: 'v{{version}}',
    build: '#{{build}}',
    copyright: '© {{year}} GeoKing',
    license: 'GPL-3.0',
  },
  nav: {
    menu: 'Menu',
    close: 'Close',
    search: 'Search payments, messages, schemes…',
    start: 'Payments',
    overview: 'Home',
    try: 'Try editor',
    live: 'Live showcase',
    map: 'Interop map',
    glossary: 'Glossary',
    about: 'About',
    privacy: 'Privacy',
    contact: 'Contact',
    standards: 'API standards',
    flows: 'Flows',
    messages: 'ISO 20022',
    schemes: 'Schemes',
    infrastructure: 'Infrastructure',
    tools: 'Tools',
    catalog: 'Technical catalog',
    quiz: 'Debug quiz',
    compareVersions: 'Compare pacs.008',
    darkMode: 'Dark mode',
    lightMode: 'Light mode',
  },
  locale: {
    label: 'Language',
    en: 'EN',
    fr: 'FR',
  },
  category: {
    'account-information': 'Account information',
    'payment-initiation': 'Payment initiation',
    'funds-confirmation': 'Funds confirmation',
    clearing: 'Clearing & settlement',
    exception: 'Exception handling',
    scheme: 'Payment schemes',
  },
  area: {
    pain: 'Payments Initiation',
    pacs: 'Payments Clearing & Settlement',
    camt: 'Cash Management',
    acmt: 'Account Management',
    auth: 'Authorities',
    remt: 'Remittance Advice',
  },
  family: {
    'iso-tx-status': 'ISO 20022 transaction status',
    'iso-status-reason': 'ISO 20022 status reason',
    'bg-error': 'Berlin Group error',
    'stet-error': 'STET error',
    'ukob-error': 'UK Open Banking error',
    'sca-status': 'SCA status',
    'consent-status': 'Consent status',
    'scheme-status': 'Scheme status (A2A overlay)',
  },
  search: {
    title: 'Search',
    placeholder: 'DbtrAgt, pacs.008, AC01, consent, recall…',
    emptyShort: 'Type at least two characters.',
    emptyNone: 'Nothing matched. Try an ISO tag (DbtrAgt), a code, a message id, or a standard name.',
    clear: 'Clear search',
    move: '↑↓ move',
    open: '↵ open',
    results: '{{count}} results',
  },
  kind: {
    standard: 'Standard',
    message: 'Message',
    flow: 'Flow',
    code: 'Code',
    sample: 'Sample',
    endpoint: 'Endpoint',
    term: 'Term',
    payment: 'Payment',
    scheme: 'Scheme',
    infrastructure: 'Rail',
    live: 'Live',
  },
  home: {
    eyebrow: 'European payments',
    title1: 'Understand how payments work.',
    title2: '',
    lead:
      'Start with how regular and instant payments differ from the CSM in the middle, then explore flows, ISO 20022 messages, schemes and rails.',
    noticeTitle: 'What this portal is',
    noticePurpose:
      'This portal gathers information about financial processes to explain standards, actors, terms, flows and related concepts.',
    noticeAi: 'Powered by AI — some errors may be present.',
    noticeNoMoney: 'No real money is involved here.',
    noticePublic: 'All data in this website are from public sources.',
    noticeInfoOnly: 'This is an informative website only.',
    searchPrompt: 'What do you want to explore?',
    explorePayment: 'Explore a payment',
    thenTrace: 'Start with the story, then open the technical trace hop by hop.',
    popular: 'Popular',
    seeAlso: 'See also',
    storyEyebrow: 'Follow €100',
    storyHeadline: 'How does €100 travel from France to Germany?',
    ctaTry: 'Try editor — pacs.008 → pacs.002',
    ctaMap: 'Open interop map',
    ctaMapHint: 'standards × rails',
    ctaSct: 'SCT Inst happy path',
    ctaAck: 'pacs.002 ack XML',
    traceFlow: 'Trace a flow',
    steps: '{{count}} steps',
    standards: 'Standards covered',
    instant: 'Instant payments (SCT Inst / IPR)',
    instantHappy: 'Happy path ≤10s',
    instantVop: 'VoP then instant (IPR)',
    instantReject: 'Reject in window',
    instantTimeout: 'Timeout & pacs.028',
    instantRecall: 'Recall after settlement',
    instantSic: 'SIC IP (CHF)',
    sctStandard: 'SCT Inst standard & gotchas →',
    inspectXml: 'Inspect ISO 20022 XML',
    blurb008: 'FI-to-FI customer credit transfer — the interbank payment itself.',
    blurb002: 'Payment status / acknowledgement — ACSC, RJCT and reason codes.',
    blurb001: 'Customer credit transfer initiation into the bank.',
    blurb056: 'Cancellation / recall request after settlement.',
    traceClearing: 'Trace pacs.008 → pacs.002 ack in clearing →',
    readId: 'Read an identifier',
    idHint:
      'Variant and version are different things. A bank on variant 001 version 09 will reject your version 08 payload, and the error you get back will rarely say so.',
    open008: 'Open pacs.008 →',
    lookCode: 'Look up a code',
    allCodes: 'All {{count}} codes →',
    footer:
      '{{payments}} payments · {{messages}} messages · {{codes}} codes. Press ⌘K to search.',
    apiLayer: 'API LAYER — WHAT THE TPP SEES',
    clearingLayer: 'CLEARING LAYER — WHAT THE BANK SEES',
    reasonStop: 'reason codes stop here',
    samePayment: 'The same payment. A TPP sees "rejected"; the bank sees AC01 and knows the IBAN is wrong.',
    overview: {
      eyebrow: 'How the system fits together',
      title: 'Regular, instant, and the CSM in between',
      lead:
        'A scheme says what must happen. A CSM is where banks clear and settle. Same pacs.008 shape — different clock, cut-off, and rail.',
      schemeTitle: 'Scheme (business rules)',
      schemeBody:
        'SCT, SCT Inst, SDD: who must be reachable, which ISO messages, which SLA. The rulebook — not the wire.',
      csmTitle: 'CSM (the rail)',
      csmBody:
        'Clearing and Settlement Mechanism: STEP2 for batch SEPA, TIPS or RT1 for instant. Banks exchange pacs here.',
      vsTitle: 'Regular vs instant',
      vsBody:
        'Regular SCT waits for the next batch / TARGET day. Instant (IPR default for euro CT) settles in ≤10 seconds, 24/7, with LclInstrm=INST.',
      linkRegular: 'Explore SCT',
      linkInstant: 'Explore SCT Inst',
      diagramEyebrow: 'Actors & money path',
      diagramHint: 'Switch the payment type — the CSM and who starts the instruction change.',
      modeLabel: 'Payment type',
      mode: {
        regular: 'Regular SCT',
        instant: 'Instant SCT Inst',
        sdd: 'Direct debit',
      },
      modeLead: {
        regular:
          'Payer originates a credit transfer. Debtor bank batches pacs.008 into STEP2 (or equivalent). Creditor sees funds next settlement cycle — not in seconds.',
        instant:
          'Same actors and roles as regular SCT, but the CSM is TIPS or RT1. Funds available in ≤10 seconds; reject or timeout must finish inside that window.',
        sdd:
          'Creditor originates the collection (pain.008). Money still leaves the Debtor and arrives at the Creditor — initiation side flips, ISO sides do not.',
      },
      partyPayer: 'Payer',
      partyBene: 'Beneficiary',
      partyCreditor: 'Creditor',
      bankDebtor: 'Debtor bank',
      bankCreditor: 'Creditor bank',
      csmNode: 'CSM',
      csmBatch: 'STEP2 / batch',
      csmInstant: 'TIPS / RT1',
      slaRegular: 'Next TARGET day',
      slaInstant: '≤10 seconds · 24/7',
      msgsRegular: 'pain.001 → pacs.008 → pacs.002',
      msgsInstant: 'pain.001 → pacs.008 INST → pacs.002 ACSC',
      msgsSdd: 'pain.008 → pacs.003 → pacs.002',
      rolesEyebrow: 'Vocabulary',
      rolesTitle: 'Debtor / Creditor vs Orig / Bene',
      rolesLead:
        'ISO 20022 labels the account sides (who is debited / credited). Operations often say originator / beneficiary for who starts or receives the instruction.',
      roleDebtor: 'Debtor',
      roleCreditor: 'Creditor',
      roleOriginator: 'Originator',
      roleBeneficiary: 'Beneficiary',
      rolePayer: 'Payer',
      roleDebtorPlain: 'Account that loses the money (Dbtr / DbtrAcct). Always the payer side economically.',
      roleCreditorPlain: 'Account that gains the money (Cdtr / CdtrAcct). Always the payee side economically.',
      roleOriginatorPlain: 'Party that starts the instruction — payer on a credit transfer, creditor on a direct debit.',
      roleBeneficiaryPlain: 'Party that receives the credit on a credit transfer (usually = Creditor).',
      mapEyebrow: 'Quick map',
      mapTitle: 'Same words, different payments',
      mapLead:
        'On SCT and SCT Inst, Originator ≈ Debtor and Beneficiary ≈ Creditor. On SDD, the Creditor is the Originator of the collection.',
      colPayment: 'Payment',
      colDebit: 'Debited',
      colCredit: 'Credited',
      colStarts: 'Who starts',
      mapSctDebit: 'Payer = Debtor = Orig',
      mapSctCredit: 'Bene = Creditor',
      mapSctStarts: 'Payer / Debtor bank',
      mapSddDebit: 'Payer = Debtor',
      mapSddCredit: 'Creditor = Orig',
      mapSddStarts: 'Creditor / Creditor bank',
      mapFoot:
        'In XML look for Dbtr / Cdtr, not “payer”. Agents are DbtrAgt / CdtrAgt. Clearing still sits in the CSM in the middle.',
    },
  },
  explorer: {
    eyebrow: 'Payment Explorer',
    scheme: 'Scheme',
    infrastructure: 'Infrastructure',
    viaLabel: 'Who starts it',
    viaHint: 'The payer at their own bank, or a payment app they authorised (a PISP).',
    viaBank: 'Bank',
    viaPisp: 'App (PISP)',
    viaWero: 'A2A overlay',
    viaMerchant: 'Merchant',
    viaCreditor: 'Creditor',
    country: 'Payer country',
    countryHint: 'Where the payer’s account is. Cut-off times and the usual network can change.',
    cutoff: 'Cut-off',
    reachability: 'Reachability',
    localException: 'Local notes',
    countryNotesLead: 'What this country changes for the payer’s bank.',
    rail: 'Which network',
    railHint: 'The system between banks that actually moves the money — not the app you tap.',
    outcome: 'How it ends',
    outcomeHint: 'The money arrives, the other bank refuses, nobody answers in time, or a bank later asks to reverse a credit (recall).',
    happy: 'Arrived',
    reject: 'Refused',
    timeout: 'No answer',
    recall: 'Recall',
    compare: 'Compare with',
    compareHint: 'Same €100, other scheme — see which hops differ.',
    compareVs: 'SCT',
    howTo:
      'Read the numbered story first. The switches change the situation. When a step is clear, open a technical trace to see the messages banks send.',
    tracesHint: 'The same journey as ISO 20022 messages. Open one when you want the sequence and the XML.',
    questionsTitle: 'Four questions',
    qWho: 'Who acts?',
    qSystem: 'Which system?',
    qMessages: 'Which messages?',
    qStandards: 'Which rules?',
    noMessage: 'No ISO message on this hop',
    thisHop: 'This hop',
    openTrace: 'See this hop as a message exchange →',
    layerTraces: 'Go deeper',
    tryInLive: 'Try in live showcase',
    messagePage: 'Message page',
    inspectSample: 'Inspect sample',
    related: 'Related',
    usedBy: 'Used by',
    sources: 'Sources',
    exploreThis: 'Explore this payment',
    vocabLabel: 'Actor labels',
    vocabHint:
      'Plain names (Payer / Beneficiary), ops shorthand (Orig / Bene), or ISO account sides (Dbtr / Cdtr). On SDD the creditor is Orig.',
    vocab: {
      plain: 'Plain',
      ops: 'Orig / Bene',
      iso: 'Dbtr / Cdtr',
    },
    actor: {
      payer: 'Payer',
      bankA: 'Payer’s bank',
      csm: 'Clearing / settlement',
      bankB: 'Beneficiary’s bank',
      beneficiary: 'Beneficiary',
      scheme: 'Scheme',
      merchant: 'Merchant',
      acquirer: 'Acquirer',
    },
  },
  compare: {
    eyebrow: 'Version compare',
    title: 'Schema revisions',
    lead:
      'Same business message, different xmlns. Markets pick a revision via usage guidelines — SEPA often stays on .08 while CBPR+ moves to .10 / .13.',
    from: 'From',
    to: 'To',
    diff: 'What changes',
    sameVersion: 'Same revision selected on both sides.',
    xmlnsDiff: 'Document namespace changes from {{from}} to {{to}}.',
    schemaDiff: 'XSD type name: {{from}} → {{to}}.',
    marketsAdded: 'Markets emphasized on the right: {{list}}.',
    marketsRemoved: 'Markets emphasized on the left only: {{list}}.',
    statusDiff: 'Catalogue status: {{from}} → {{to}}.',
    backMessage: 'Open message page →',
  },
  quiz: {
    eyebrow: 'Debug this payment',
    title: 'Payment Debugging Quiz',
    lead:
      'Practice payment troubleshooting across ISO 20022 clearing messages and Open Banking API responses. Read the payload and pick the correct reason code or error code.',
    progress: 'Question {{current}} of {{total}}',
    score: 'Score: {{score}} / {{total}}',
    prompt: 'Select the code present in the payload',
    correct: 'Correct!',
    incorrect: 'Incorrect.',
    prev: '← Previous',
    next: 'Next question →',
    restart: 'Restart quiz',
    completedTitle: 'Quiz completed!',
    completedLead: 'You scored {{score}} out of {{total}} ({{percent}}%).',
    openExplorer: 'Explore related flow →',
    payload: 'Inspected payload sample',
    openEditor: 'Open in Try editor →',
  },
  map: {
    eyebrow: 'Interop map',
    title: 'Standards, messages and rails',
    lead:
      'A Cytoscape network of how XS2A / Open Banking standards connect to ISO 20022 messages and clearing rails (SEPA, SIC, euroSIC, SIC IP, Wero). Use a flow page for ordered swimlanes; use this map for the topology.',
  },
  glossary: {
    eyebrow: 'Glossary',
    title: 'Payments glossary',
    lead:
      'Short definitions for the words that keep turning up in PSD2, SCT Inst, Open Banking and ISO 20022 — including every status and reason code, and whether a retry is legitimate. Switch language for the French naming.',
    sourcesLead: 'Aligned with public glossaries (UK Open Banking, Mastercard Open Finance US, Konsentus Europe, Bundesbank PSD2, Ravelin PSD acronyms). Full citations on',
    placeholder: 'SCT, TIPS, PIIS, VoP, AC01…',
    filterAria: 'Filter glossary',
    all: 'All {{count}}',
    empty: 'Nothing matched. Try SCT, TIPS, VoP, AC01, or a fragment like "consent".',
    alsoCalled: 'Also called',
    seeAlso: 'See also',
    relatedFlows: 'Related flows',
    explore: 'Explore in the catalog',
    alignedWith: 'Aligned with',
    action: 'What to do',
  },
  about: {
    eyebrow: 'About',
    title: 'About this explorer',
    lead:
      'A payment crosses a border halfway through its life: JSON over HTTPS on the TPP side, ISO 20022 XML on the bank side. This site holds both vocabularies in one catalog.',
    glossaryTitle: 'Glossary sources',
    glossaryLead:
      'Terms, codes and acronyms in the glossary are written for integrators. Where an entry maps onto a public industry glossary, the source is linked on the entry and listed here.',
    glossaryCount: '{{count}} entries in the catalog.',
    glossaryNote:
      'Mastercard entries describe Open Finance US product vocabulary (Data Connect, FinBanks, Partner Linked). They sit beside EU/UK PSD2 terms so the same catalog covers both dialects — they are not interchangeable legal definitions.',
    publisher:
      'OpenFinance is published by GeoKing. It is an educational catalog of European payment schemes, infrastructures and ISO 20022 messages. It is not a bank, a payment service, or financial, legal or investment advice.',
  },
  privacy: {
    eyebrow: 'Legal',
    title: 'Privacy policy',
    updated: 'Last updated: 17 August 2026.',
    who: 'This site (openfinance.geoking.fr) is operated by GeoKing. It explains how European payment flows, SEPA schemes and ISO 20022 messages work. We do not create user accounts and we do not ask you to send us payment credentials.',
    dataTitle: 'What we store',
    data: 'In your browser we may keep a language preference, a light/dark theme, and your AdSense personalization choice (accept or non-personalized only) in local storage. The site does not run a first-party analytics product. Server logs on Cloudflare may include a truncated IP address, user-agent and the URL you requested, used only to operate and secure the site.',
    adsTitle: 'Advertising',
    ads: 'We use Google AdSense to show ads on catalog pages that contain editorial explanations. Google and its advertising partners may use cookies or similar technologies to serve personalized or non-personalized ads, measure performance and prevent fraud. Before personalized ads load, a site banner asks for your consent. If you refuse, we request non-personalized ads only. You can also use Google Funding Choices messages when Google shows them for the EEA, UK or Switzerland.',
    googlePolicy: 'Google Privacy Policy',
    cookiesTitle: 'Cookies',
    cookies: 'AdSense may set cookies on our domain and on Google domains after you choose personalized or non-personalized ads. They are not required to read the catalog. Blocking them may hide ads; the articles remain available.',
    contactTitle: 'Contact',
    contact: 'Questions about this policy or about personal data:',
  },
  contact: {
    eyebrow: 'Publisher',
    title: 'Contact',
    lead: 'Write to the publisher if you have a question about OpenFinance, this catalog, or how we use AdSense on the site.',
    emailLabel: 'Email:',
    publisher: 'GeoKing publishes OpenFinance as an educational website. We cannot process payments, open accounts, or answer questions about a specific bank transfer.',
  },
  notFound: {
    title: 'Nothing at this path',
    body: 'The standard, flow or message you asked for is not in the catalog. Search with ⌘K to find what is.',
    back: 'Back to overview',
  },
  flow: {
    txFlow: 'Transaction flow',
    sequence: 'Sequence',
    diagramType: 'Diagram type',
    entities: 'Entities',
    participation: 'Clearing membership',
    directParticipant: 'Direct',
    subParticipant: 'Sub-participant',
    directParticipantHint:
      'Direct clearing member: ILM liquidity checks stay on the path before settlement.',
    subParticipantHint:
      'Pre-funded sub-participant: ILM legs are skipped — the hub goes straight to core banking / settlement.',
    clickStep: 'Click a step, or tab to it and press enter',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    apiHop: 'API hop',
    clearingHop: 'Clearing hop',
    clickArrow: 'Click an arrow to inspect the hop',
    clickMessage: 'Click a tag on an arrow (pacs.008, …) for info, usage and sample',
    runScenario: 'Run scenario',
    pause: 'Pause',
    restart: 'Restart',
    playback: 'Playback',
    messages: 'Messages',
    technicalViewOf: 'Technical view of',
    messagePage: 'Full detail →',
    usageHere: 'This flow',
    usageOther: 'Also used in',
    noPayload: 'No payload',
    noPayloadBody:
      'This step is a redirect, a wait or an out-of-band event — there is no message on the wire to inspect. Steps with a payload are marked with a method or a message id in the diagram.',
    openSample: 'Open sample page →',
    allSamples: 'All {{short}} samples →',
    openTry: 'Try editor — open message samples →',
    useCase: 'Why read this',
    tryInLive: 'Try in live showcase',
    seeStepInLive: 'See this step in live →',
    samples: 'Samples in this flow',
    prev: '← Previous',
    next: 'Next step →',
    headers: 'Headers that matter',
    codesHere: 'Codes you can see here',
    message: 'Message',
  },
  message: {
    facts: 'Facts',
    root: 'Root element',
    direction: 'Direction',
    namespace: 'Namespace',
    variant: 'Variant (flavour)',
    version: 'Version',
    country: 'Country',
    guideline: 'Usage guideline',
    versions: 'Schema versions',
    versionsLead:
      'SWIFT and ISO publish successive XSD revisions of the same business message (e.g. pacs.008.001.08 → .10 → .13; same pattern for camt and pain). Markets pin one via a usage guideline (EPC SEPA, CBPR+, Swiss SPS…) and may add a country suffix (pacs.008.001.08.ch.02) — the Document xmlns must match that XSD.',
    versionsEditLead:
      'Pick the schema revision, flavour (variant), or national guideline you are targeting. The editor updates the Document xmlns (and the paired response) to match.',
    variantShort: 'var {{variant}}',
    versionsXsd: 'ISO catalogue (XSD)',
    versionsSwift: 'SWIFT / CBPR+',
    versionsGlossary: 'SWIFT & versioning glossary',
    markets: 'Markets / rails',
    versionCurrent: 'current',
    versionLegacy: 'legacy',
    versionUpcoming: 'upcoming',
    sampleVersionMismatch:
      'Bundled samples still use another xmlns; none match {{version}} yet. Inspect the payload carefully before sending to a bank on this revision.',
    required: 'Elements a validator will insist on',
    requiredHint: 'Not the full XSD — the paths whose absence causes the rejections you actually see in production.',
    startWithStory: 'Start with the payment story',
    appearsIn: 'Then the technical flows',
    usage: 'Usage in flows',
    openSample: 'Open sample page →',
    editHint: 'Edit in the XML / JSON tab, then run the check.',
    modified: 'Modified —',
    reset: 'reset',
    noSample: 'No sample bundled for {{short}} yet. Add one to src/data/samples.ts and it appears here, in search, and in any flow step that references it.',
    dirCustomerBank: 'Customer → bank',
    dirBankCustomer: 'Bank → customer',
    dirBankBank: 'Bank → bank',
    dirBankCsm: 'Bank → clearing',
    ackEyebrow: 'acknowledgement',
    ackNote:
      'This is the clearing acknowledgement: TxSts carries ACSC (settled), RJCT (refused) and friends. Pair it with the original pacs.008, or build both in the Try editor.',
    build008: 'Build a custom pacs.008 and see the matching pacs.002 acknowledgement in the Try editor.',
    openEditor: 'Open in Try editor →',
    workspaceMode: 'Workspace mode',
    modeView: 'View',
    modeEdit: 'Edit',
    helpMode: 'Help',
    schemaForm: 'Schema fields',
    schemaUnavailable: 'No XSD model for this message yet — edit XML in the inspector.',
    schemaNeedXml: 'Provide well-formed XML to edit schema fields.',
    schemaAdd: 'Add {{name}}',
    schemaRemove: 'Remove',
    schemaRequiredMissing: 'required',
    xmlJsonHint: 'Switch XML | JSON in the inspector without leaving this sample.',
  },
  try: {
    eyebrow: 'Try editor',
    title: 'Build a pacs.008, get a pacs.002',
    titleGeneric: 'Edit {{short}}',
    lead:
      'Fill the payment fields. Focus on the pacs.008 you would send; reveal the pacs.002 acknowledgement (or reject) when you want it. Toggle XML | JSON, edit freely, then validate.',
    leadGeneric:
      'Start from a synthetic sample, edit the XML freely, then validate against the catalog required paths. Paired responses (e.g. pain.002) stay hidden until you show them.',
    showResponse: 'Show {{short}}',
    hideResponse: 'Hide {{short}}',
    messageType: 'Message type',
    rootElement: 'Root element',
    sampleVariant: 'Sample',
    catalogPage: 'Catalog page',
    tagBuilder: 'builder',
    tagSample: 'sample',
    titlePrimary: '{{short}} — message you edit',
    titlePair: '{{short}} — paired response',
    editPrimary: 'Edit in the XML / JSON tab. Use Reset to restore the sample.',
    editPair: 'Edit the paired response sample. Identifiers may not match your primary edits unless you align them manually.',
    titleStructured: 'Build {{primary}}, get {{pair}}',
    fields: 'Payment fields',
    reset: 'Reset',
    instant: 'SCT Inst (LclInstrm=INST)',
    instantDef:
      'Sets Local Instrument INST — SCT Instant / IPR path. Affects PmtTpInf in pacs.008.',
    clearing: 'Clearing system',
    clearingDef: 'Settlement system reported in SttlmInf/ClrSys (TIPS, RT1, STEP2, SIC, euroSIC).',
    outcome: 'pacs.002 outcome',
    outcomeDef: 'What the creditor agent would return: settled (ACSC) or rejected (RJCT + reason).',
    ack: 'ACSC ack',
    reject: 'RJCT',
    reason: 'Reason code',
    reasonDef: 'ISO external status reason (e.g. AC01 wrong IBAN, AM04 insufficient funds, AB05 timeout).',
    related: 'Related references:',
    edit008: 'Edit in the XML / JSON tab. Changing form fields regenerates unless you have local edits — use Reset to clear them.',
    ack002: 'Generated from the same identifiers as the pacs.008 so EndToEndId / TxId / OrgnlMsgId line up for reconciliation.',
    title008: 'pacs.008 — credit transfer you build',
    title002Ack: 'pacs.002 — ACSC acknowledgement',
    title002Rjct: 'pacs.002 — RJCT ({{code}})',
    sectionMoney: 'Money',
    sectionIds: 'Identifiers',
    sectionDebtor: 'Debtor',
    sectionCreditor: 'Creditor',
    sectionRemittance: 'Remittance',
    sectionTiming: 'Timing',
    sectionClearing: 'Clearing',
    fieldAmount: 'Amount',
    fieldCurrency: 'Currency',
    fieldEndToEndId: 'EndToEndId',
    fieldInstructionId: 'InstrId',
    fieldTxId: 'TxId',
    fieldMsgId: 'MsgId',
    fieldDebtorName: 'Debtor name',
    fieldDebtorIban: 'Debtor IBAN',
    fieldDebtorBic: 'Debtor BIC',
    fieldCreditorName: 'Creditor name',
    fieldCreditorIban: 'Creditor IBAN',
    fieldCreditorBic: 'Creditor BIC',
    fieldRemittance: 'Remittance',
    fieldSettlementDate: 'Settlement date',
    fieldCreatedAt: 'Created at',
    defAmount: 'IntrBkSttlmAmt — interbank settlement amount. Use a decimal with a dot (42.50).',
    defCurrency: 'ISO 4217 currency on IntrBkSttlmAmt (EUR, CHF, …).',
    defEndToEndId: 'End-to-end reference kept end-to-end across banks (PmtId/EndToEndId). Max 35 chars.',
    defInstructionId: 'Instruction identification assigned by the instructing party (PmtId/InstrId).',
    defTxId: 'Unique transaction id assigned by the first instructing agent (PmtId/TxId).',
    defMsgId: 'Message identification of this pacs.008 (GrpHdr/MsgId). Referenced as OrgnlMsgId in pacs.002.',
    defDebtorName: 'Dbtr/Nm — name of the party whose account is debited.',
    defDebtorIban: 'DbtrAcct IBAN — debtor account. FR76… reserved test range is fine here.',
    defDebtorBic: 'DbtrAgt BICFI — debtor agent (sending bank).',
    defCreditorName: 'Cdtr/Nm — party whose account is credited.',
    defCreditorIban: 'CdtrAcct IBAN — creditor account.',
    defCreditorBic: 'CdtrAgt BICFI — creditor agent (receiving bank).',
    defRemittance: 'RmtInf/Ustrd — unstructured remittance shown to the beneficiary.',
    defSettlementDate: 'IntrBkSttlmDt — calendar date of interbank settlement (YYYY-MM-DD).',
    defCreatedAt: 'CreDtTm / AccptncDtTm — when the message was created (ISO-8601 UTC).',
    datePresets: 'Quick dates',
    dateYesterday: 'Yesterday',
    dateToday: 'Today',
    dateTomorrow: 'Tomorrow',
    dateNow: 'Now',
    dateMinus1h: '−1h',
    datePlus1h: '+1h',
    dateMinus1d: '−1d',
    datePlus1d: '+1d',
    dateStartToday: '00:00 UTC',
    dateNoonToday: '12:00 UTC',
    paneMode: 'XML panes',
    paneBoth: 'Both',
    modeSimple: 'Simple',
    modeExpert: 'Expert',
    expertHint:
      'Every leaf in the generated XML. Click a tag in the tree to focus its field; focus a field to highlight the tag.',
    sectionDocument: 'Document',
    sectionGrpHdr: 'GrpHdr',
    sectionPmtId: 'PmtId',
    sectionPmtTpInf: 'PmtTpInf',
    sectionTxInf: 'CdtTrfTxInf',
    sectionStatus: 'pacs.002',
    fieldXmlns: 'xmlns',
    fieldNbOfTxs: 'NbOfTxs',
    fieldTtlAmount: 'TtlIntrBkSttlmAmt',
    fieldTtlCurrency: 'Ttl Ccy',
    fieldGrpSettlementDate: 'GrpHdr IntrBkSttlmDt',
    fieldSttlmMtd: 'SttlmMtd',
    fieldInstgAgt: 'InstgAgt BICFI',
    fieldInstdAgt: 'InstdAgt BICFI',
    fieldSvcLvl: 'SvcLvl',
    fieldLclInstrm: 'LclInstrm',
    fieldChrgBr: 'ChrgBr',
    fieldAccptncDtTm: 'AccptncDtTm',
    fieldStatusMsgId: 'pacs.002 MsgId',
    fieldOrgnlMsgNmId: 'OrgnlMsgNmId',
    fieldAddtlInf: 'AddtlInf',
    defXmlns: 'Document namespace — must match the schema revision the CSM expects.',
    defNbOfTxs: 'GrpHdr/NbOfTxs — number of transactions in this message.',
    defTtlAmount: 'GrpHdr/TtlIntrBkSttlmAmt — sum of interbank amounts. Independent from the Tx Inf amount in expert mode.',
    defTtlCurrency: 'Ccy on TtlIntrBkSttlmAmt.',
    defGrpSettlementDate: 'GrpHdr/IntrBkSttlmDt — group-level settlement date.',
    defSttlmMtd: 'SttlmInf/SttlmMtd — CLRG (clearing), INDA, INGA or COVE.',
    defInstgAgt: 'Instructing agent BIC (GrpHdr/InstgAgt). Often the debtor agent.',
    defInstdAgt: 'Instructed agent BIC (GrpHdr/InstdAgt). Often the creditor agent.',
    defSvcLvl: 'PmtTpInf/SvcLvl/Cd — service level, typically SEPA.',
    defLclInstrm: 'PmtTpInf/LclInstrm/Cd — INST for SCT Instant. Empty omits the element.',
    defChrgBr: 'Charge bearer: SLEV (following service level), DEBT, CRED, SHAR.',
    defAccptncDtTm: 'CdtTrfTxInf/AccptncDtTm — when the debtor agent accepted the instruction.',
    defStatusMsgId: 'pacs.002 GrpHdr/MsgId — identification of the status report itself.',
    defOrgnlMsgNmId: 'OrgnlMsgNmId — full versioned id of the original pacs.008.',
    defAddtlInf: 'StsRsnInf/AddtlInf — free-text complement on a RJCT.',
    pain: {
      lead: 'Customer credit transfer initiation. Fill the fields to build a pain.001; the paired pain.002 shows ACTC acceptance or RJCT with reason.',
      sectionPmtInf: 'PmtInf',
      sectionStatus: 'pain.002',
      fieldCtrlSum: 'CtrlSum',
      fieldInitgPty: 'Initiating party',
      fieldPmtInfId: 'PmtInfId',
      fieldPmtMtd: 'PmtMtd',
      fieldExecutionDate: 'Requested execution date',
      fieldStatusMsgId: 'pain.002 MsgId',
      outcome: 'pain.002 outcome',
      outcomeDef: 'Group / transaction status the debtor bank returns: accepted (ACTC) or rejected (RJCT).',
      outcomeACTC: 'ACTC',
      outcomeRJCT: 'RJCT',
      defMsgId: 'GrpHdr/MsgId — message id of this pain.001. Referenced as OrgnlMsgId in pain.002.',
      defAmount: 'InstdAmt — instructed amount (decimal with dot).',
      defCtrlSum: 'CtrlSum — control sum at group and payment-info level.',
      defInitgPty: 'InitgPty/Nm — party that initiated the file (PISP or corporate).',
      defPmtInfId: 'PmtInf/PmtInfId — payment information block id.',
      defPmtMtd: 'PmtMtd — TRF for credit transfer.',
      defExecutionDate: 'ReqdExctnDt — date the debtor bank should execute.',
      defStatusMsgId: 'pain.002 GrpHdr/MsgId.',
      defOrgnlMsgNmId: 'OrgnlMsgNmId — full versioned id of the original pain.001.',
    },
    camt: {
      lead: 'Recall / cancellation request (camt.056) and resolution (camt.029). Quote the original pacs.008 identifiers so both banks match the case.',
      sectionCase: 'Case',
      sectionBanks: 'Assignee / assignor',
      sectionOriginal: 'Original payment',
      sectionReason: 'Cancellation reason',
      sectionAssignment: 'Assignment',
      sectionResolution: 'camt.029 resolution',
      sectionStatus: 'camt.029',
      fieldAssignmentId: 'Assignment id',
      fieldCaseId: 'Case id',
      fieldAssignorBic: 'Assignor BIC',
      fieldAssigneeBic: 'Assignee BIC',
      fieldOrgnlMsgId: 'Original MsgId',
      fieldCxlId: 'CxlId',
      fieldCxlReason: 'Cancellation reason',
      fieldResolutionId: 'Resolution id',
      fieldCxlStsId: 'CxlStsId',
      outcome: 'Resolution',
      outcomeDef: 'ACCR — recall accepted; RJCR — recall rejected.',
      outcomeACCR: 'ACCR',
      outcomeRJCR: 'RJCR',
      defAssignmentId: 'Assgnmt/Id — unique id for this cancellation request.',
      defCaseId: 'Case/Id — investigation case both banks quote.',
      defAssignorBic: 'Assgnr — bank sending the recall.',
      defAssigneeBic: 'Assgne — bank receiving the recall.',
      defOrgnlMsgId: 'OrgnlMsgId from the original pacs.008.',
      defOrgnlMsgNmId: 'OrgnlMsgNmId — versioned message name of the original payment.',
      defOrgnlAmount: 'OrgnlIntrBkSttlmAmt — amount from the original transfer.',
      defCxlId: 'CxlId — cancellation instruction id.',
      defCxlReason: 'CxlRsnInf/Rsn/Cd — DUPL, FRAD, TECH…',
      defAddtlInf: 'Free-text complement on the recall reason.',
      defResolutionId: 'camt.029 Assgnmt/Id.',
      defCxlStsId: 'CxlStsId in the resolution message.',
    },
    acmt: {
      lead: 'Verification of Payee request (acmt.023) and report (acmt.024). Send the name exactly as the PSU typed it.',
      sectionParty: 'Party to verify',
      sectionReport: 'acmt.024 report',
      sectionStatus: 'acmt.024',
      fieldVrfId: 'Verification id',
      fieldReportId: 'Report MsgId',
      fieldUpdatedName: 'Updated name',
      fieldReasonCode: 'Reason code',
      outcome: 'Match result',
      outcomeDef: 'MTCH — full match; NMTC — no match / close match with suggested name.',
      outcomeMTCH: 'MTCH',
      outcomeNMTC: 'NMTC',
      defAssignmentId: 'Assgnmt/MsgId — VoP request id.',
      defVrfId: 'Vrfctn/Id — verification reference.',
      defAssignorBic: 'Requesting bank BIC.',
      defAssigneeBic: 'Responding bank BIC.',
      defCreditorName: 'Name as entered by the PSU — do not normalise here.',
      defCreditorIban: 'Creditor IBAN to verify.',
      defReportId: 'acmt.024 Assgnmt/MsgId.',
      defUpdatedName: 'UpdtdPtyAndAcctId/Nm — suggested legal name on close match.',
      defReasonCode: 'Rsn/Cd when Vrfctn is false (e.g. CMTC).',
    },
  },
  sample: {
    iso: 'ISO 20022 sample',
    api: 'API sample',
    highlighting: 'Highlighting {{tag}} in the tree',
    usedInFlows: 'Used in flows',
    seeInLive: 'See in live showcase',
    openEditor: 'Open in Try editor →',
  },
  payload: {
    isoPayload: 'ISO 20022 payload',
    apiPayload: 'API payload',
    tree: 'tree',
    raw: 'raw',
    validate: 'validate',
    filterXml: 'Filter tags, e.g. DbtrAgt, TxSts…',
    filterJson: 'Filter keys, e.g. DbtrAgt, TxSts…',
    filterAria: 'Filter payload',
    notWellFormed: 'Not well-formed: {{error}}',
    invalidJson: 'Invalid JSON: {{error}}',
    checking: 'Checking against schema…',
    needXml: 'Validation needs XML. Switch to the XML view or provide an XML source.',
    unreachable: 'Validator unreachable. Run `npm start` so Pages Functions serve /api/validate.',
    runAgain: 'Run check again',
    passed: 'Schema checks passed.',
    issues: '{{count}} issue(s).',
    detected: 'Detected {{short}}',
    showInTree: 'Show in XML tree',
    elements: '{{count}} elements',
    depth: 'depth {{n}}',
    chars: '{{count}} chars',
    matches: '{{count}} matches',
    copy: 'Copy payload',
    copied: 'Copied',
  },
  standard: {
    publisherDocs: 'Publisher docs ↗',
    security: 'Security profile',
    clientAuth: 'Client authentication',
    messageSigning: 'Message signing',
    tokens: 'Tokens',
    certificates: 'Certificates',
    sca: 'SCA approaches',
    gotchas: 'What bites integrators',
    apis: 'APIs',
    flows: 'Flows',
    samples: 'Samples',
    apisEndpoints: 'APIs and endpoints',
    flowsUsing: 'Flows using this standard',
    samplePayloads: 'Sample payloads',
    steps: '{{count}} steps',
  },
  live: {
    eyebrow: 'Real-life showcase',
    title: 'Payments as they happen',
    lead:
      'Walk a checkout, a subscription, a P2P send or receive, and a bank inbox. Each step shows the consumer screen and the ISO / API payload underneath — without changing the catalog pages.',
    backHub: 'All scenes',
    explainer: 'What is happening',
    openFlow: 'Open full diagram',
    openPayment: 'Payment explorer',
    openSample: 'Open sample page →',
    openEditor: 'Open in Try editor →',
    alexSide: 'Alex’s side (send)',
    marieSide: 'Marie’s side (receive)',
    seeBanks: 'See what the banks did',
    noPayload: 'No sample on this beat',
    noPayloadBody: 'This step is UI-only or waits on the next hop. Advance to load a payload.',
    beat: 'Beat',
    beatsNav: 'Scenario steps',
    prev: 'Previous',
    next: 'Next',
    beatN: 'Step {{n}}',
    statusOk: 'Settled',
    statusBad: 'Failed',
    statusIdle: 'In progress',
    paid: 'Paid',
    failed: 'Failed',
    noxPlan: 'Nox Standard · €12.99 / month',
    scaTitle: 'Strong customer authentication',
    cta: {
      checkout: 'Pay now',
      approve: 'Approve',
      mandate: 'Sign mandate',
      send: 'Send',
    },
    method: {
      bank: 'Pay by bank',
      instant: 'Instant',
      wero: 'Wero',
      card: 'Card',
    },
    screen: {
      cart: 'Cart',
      method: 'Payment method',
      sca: 'Bank SCA',
      processing: 'Processing',
      receipt: 'Receipt',
      failed: 'Failed',
      mandate: 'Mandate',
      compose: 'Send',
      notify: 'Notification',
      incoming: 'Incoming',
      credited: 'Credited',
      inbox: 'Inbox',
      consent: 'Consent',
      statement: 'Statement',
      hub: 'Bank hub',
    },
    scene: {
      shop: 'Shop',
      stream: 'Streaming / store',
      wallet: 'Send',
      receive: 'Receive',
      bank: 'Bank',
    },
    outcome: {
      happy: 'Happy',
      reject: 'Reject',
      timeout: 'Timeout',
      recall: 'Recall',
      cancel: 'Cancel',
    },
  },
};
